import { movies as rawMovies } from "@/mocks/movies";
import { episodesByMovieId } from "@/mocks/episodes";
import type { Movie, MovieFilter } from "./types";

export const allMovies: Movie[] = (rawMovies as unknown as Movie[]).map((movie) => ({
  ...movie,
  episodes: episodesByMovieId[movie.id],
}));

export const featuredMovies: Movie[] = allMovies.filter((movie) => movie.featured);

const unique = (values: string[]): string[] =>
  Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, "zh-CN"));

export const allGenres = unique(allMovies.flatMap((movie) => movie.genres));
export const allLanguages = unique(allMovies.map((movie) => movie.language));
export const allYears = Array.from(new Set(allMovies.map((movie) => String(movie.year)))).sort(
  (a, b) => Number(b) - Number(a),
);
export const allRegions = unique(allMovies.map((movie) => movie.region));
export const allTypes = unique(allMovies.map((movie) => movie.type));

export const DEFAULT_FILTER: MovieFilter = {
  keyword: "",
  type: "全部",
  region: "全部",
  genre: "全部",
  year: "全部",
  language: "全部",
  sort: "default",
};

export const sortOptions = [
  { value: "default", label: "默认排序" },
  { value: "updated", label: "更新日期" },
  { value: "added", label: "最新收录" },
  { value: "rating", label: "评分最高" },
  { value: "views", label: "热度最高" },
];

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return allMovies.find((movie) => movie.id === id);
}

export function matchesKeyword(movie: Movie, keyword: string): boolean {
  const word = keyword.trim().toLowerCase();
  if (!word) return true;
  const haystack = [
    movie.title,
    movie.country,
    movie.director,
    movie.summary,
    movie.tagline,
    movie.genres.join(" "),
    movie.tags.join(" "),
    movie.cast.join(" "),
    movie.plot.join(" "),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(word);
}

export function filterMovies(filter: MovieFilter, source: Movie[] = allMovies): Movie[] {
  const result = source.filter((movie) => {
    if (filter.type !== "全部" && movie.type !== filter.type) return false;
    if (filter.region !== "全部" && movie.region !== filter.region) return false;
    if (filter.genre !== "全部" && !movie.genres.includes(filter.genre)) return false;
    if (filter.year !== "全部" && String(movie.year) !== filter.year) return false;
    if (filter.language !== "全部" && movie.language !== filter.language) return false;
    if (!matchesKeyword(movie, filter.keyword)) return false;
    return true;
  });

  return sortMovies(result, filter.sort);
}

export function sortMovies(list: Movie[], sort: string): Movie[] {
  const copied = [...list];
  switch (sort) {
    case "updated":
      return copied.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    case "added":
      return copied.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    case "rating":
      return copied.sort((a, b) => b.rating - a.rating);
    case "views":
      return copied.sort((a, b) => b.views - a.views);
    default:
      return copied.sort((a, b) => {
        if (Boolean(b.featured) !== Boolean(a.featured)) return b.featured ? 1 : -1;
        return b.views - a.views;
      });
  }
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function getRelatedMovies(movie: Movie, limit = 3): Movie[] {
  return allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      const score =
        sharedGenres * 3 +
        (item.region === movie.region ? 1 : 0) +
        (item.type === movie.type ? 1 : 0) +
        item.rating / 10;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function formatViews(views: number): string {
  if (views >= 100) return `${Math.round(views)} 万`;
  return `${views.toFixed(1)} 万`;
}

export function formatDate(value: string): string {
  const [year, month, day] = value.split("-");
  return `${year}.${month}.${day}`;
}

export function countActiveFilters(filter: MovieFilter): number {
  const keys: (keyof MovieFilter)[] = ["type", "region", "genre", "year", "language"];
  return keys.filter((key) => filter[key] !== "全部").length;
}